import { useEffect, useRef, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  TextField,
  InputAdornment,
  IconButton,
  ListSubheader,
  Button,
  Badge,
  Box,
  ListItemText,
  Tooltip,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import TuneIcon from '@mui/icons-material/Tune'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import { useAppStore } from '@/store/appStore'
import { useContentBundle } from '@/hooks/useContentBundle'
import { BookmarksMenu } from './BookmarksMenu'
import { MediaGalleryButton } from './MediaGalleryButton'
import { ForceRefreshBundleButton } from '@/components/content/ForceRefreshBundleButton'
import { FeedbackButton } from '@/components/feedback/FeedbackButton'
import { clientSearch, highlightSearchTerm } from '@/utils/clientSearch'
import { filterBarSelectSx, getMultiSelectMenuItemSx } from '@/theme/muiTheme'
import { LevelType, ModelCategory } from '@/types/api'
import type { ApiCustomView, ApiSearchResult } from '@/types/api'
import { isOwnedView } from '@/utils/customViews'
import { useSharedCustomViewsEnabled } from '@/hooks/useSharedCustomViewsEnabled'
import { TreeChevronIcon } from '@/components/TreeChevronIcon'
import { useIsCompact } from '@/hooks/useLayoutMode'
import { FilterSheet } from './FilterSheet'
import { useGuardedNavigate } from '@/hooks/useGuardedNavigate'

export function FilterBar() {
  const { t } = useTranslation()
  const navigate = useGuardedNavigate()
  const sharingEnabled = useSharedCustomViewsEnabled()
  const isCompact = useIsCompact()
  const [filterSheetOpen, setFilterSheetOpen] = useState(false)
  // At compact the search box takes the whole toolbar row when open: a 360px bar
  // cannot hold the filter button, three action buttons and a usable input at once.
  const [searchExpanded, setSearchExpanded] = useState(false)

  // ─── Collapsible category state (local, resets on dropdown close) ─────────
  const [collapsedCategories, setCollapsedCategories] = useState<Set<ModelCategory>>(new Set())
  const [modelViewOpen, setModelViewOpen] = useState(false)
  const headerClickInProgressRef = useRef(false)
  const toggleCategory = (cat: ModelCategory, e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    headerClickInProgressRef.current = true
    setCollapsedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }
  const {
    selectedModelViewId,
    selectedLevelId,
    selectedContentTagIds,
    customViews,
    selectedCustomViewId,
    searchFilteredOnly,
    setSelectedModelViewId,
    setSelectedLevelId,
    setSelectedContentTagIds,
    setSelectedCustomViewId,
    requestGuardedAction,
    setSearchFilteredOnly,
  } = useAppStore()

  const { data: bundle } = useContentBundle()

  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false)

  const searchWrapRef = useRef<HTMLDivElement>(null)
  const prevContentTagIdsRef = useRef<number[]>([])

  useEffect(() => {
    prevContentTagIdsRef.current = selectedContentTagIds
  }, [selectedContentTagIds])

  // Debounce search value 400ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchValue), 400)
    return () => clearTimeout(timer)
  }, [searchValue])

  // Derive single contentTagId for search — only when exactly one specific tag is selected
  const searchContentTagId =
    searchFilteredOnly && selectedContentTagIds.length === 1 && !selectedContentTagIds.includes(-1)
      ? selectedContentTagIds[0]
      : undefined

  const inlineResults: ApiSearchResult[] = bundle && debouncedSearch.length >= 2
    ? clientSearch(bundle, debouncedSearch, 5, searchFilteredOnly ? {
        modelViewId: selectedModelViewId ?? undefined,
        levelId: selectedLevelId ?? undefined,
        contentTagId: searchContentTagId,
      } : undefined)
    : []

  const showSearchDropdown = searchDropdownOpen && debouncedSearch.length >= 2

  // Close dropdowns on outside click
  const handleDocumentMouseDown = useCallback((e: MouseEvent) => {
    if (!searchWrapRef.current?.contains(e.target as Node)) {
      setSearchDropdownOpen(false)
    }
  }, [setSearchDropdownOpen])

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentMouseDown)
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown)
  }, [handleDocumentMouseDown])

  // Navigate to result
  const handleResultClick = (result: ApiSearchResult) => {
    setSearchDropdownOpen(false)
    setSearchValue('')

    // Handle AdditionalReadingContent categories
    const contentTypeToOverlay: Record<string, 'overview' | 'appendices' | 'release-notes' | 'faq' | 'crosswalks'> = {
      'Overview': 'overview',
      'Appendices': 'appendices',
      'ReleaseNotes': 'release-notes',
      'Faq': 'faq',
      'Crosswalks': 'crosswalks',
    }

    if (result.contentType in contentTypeToOverlay) {
      const overlayType = contentTypeToOverlay[result.contentType]
      useAppStore.getState().setOverlayPage({
        type: overlayType,
        hash: `#reading-${result.entityId}`
      })
      return
    }

    // Handle traditional navigation
    const highlightHash = debouncedSearch ? `#highlight=${encodeURIComponent(debouncedSearch)}` : ''
    if (result.contentType === 'PracticeArea') {
      navigate(`/practice-area/${result.entityId}${highlightHash}`)
    } else if (result.contentType === 'Practice') {
      navigate(`/practice/${result.entityId}${highlightHash}`)
    } else if (result.contentType === 'Framework') {
      navigate(`/framework/${result.entityId}`)
    } else if (result.contentType === 'GlossaryTerm') {
      navigate(`/glossary#term-${result.entityId}`)
    } else if (result.contentType === 'ContextualContent') {
      const ccHighlight = debouncedSearch ? `&highlight=${encodeURIComponent(debouncedSearch)}` : ''
      if (result.parentPracticeId) {
        navigate(`/practice/${result.parentPracticeId}#cc-${result.entityId}${ccHighlight}`)
      } else if (result.practiceAreaId) {
        navigate(`/practice-area/${result.practiceAreaId}#cc-${result.entityId}${ccHighlight}`)
      }
    } else {
      navigate(`/search?q=${encodeURIComponent(debouncedSearch)}`)
    }
  }

  // ─── Derive effective level mode ─────────────────────────────────────────
  // MDDAP mode is active only when every selected model view is MDDAP.
  // Everything else (all models, custom view, domain/capability area, mixed) → numeric.
  const isCustomViewActive = selectedCustomViewId !== null

  const effectiveLevelMode: 'numeric' | 'mddap' = (() => {
    if (isCustomViewActive || selectedModelViewId === null) return 'numeric'
    const category = bundle?.modelViews.find((mv) => mv.id === selectedModelViewId)?.category ?? null
    return category === ModelCategory.Mddap ? 'mddap' : 'numeric'
  })()

  // ─── Compute visible practice area IDs for dynamic level cap ─────────────
  const visiblePaIds: Set<number> = (() => {
    if (!bundle) return new Set()
    if (isCustomViewActive) {
      const cv = customViews.find((c) => c.id === selectedCustomViewId)
      return new Set(cv?.practiceAreas.map((cvpa) => cvpa.practiceAreaId) ?? [])
    }
    if (selectedModelViewId === null) {
      return new Set(bundle.practiceAreas.map((pa) => pa.id))
    }
    return new Set(
      bundle.modelViewPracticeAreas
        .filter((mvpa) => mvpa.modelViewId === selectedModelViewId)
        .map((mvpa) => mvpa.practiceAreaId)
    )
  })()

  // ─── Compute max numeric level sequence available in visible PAs ──────────
  const maxNumericSequence: number = (() => {
    if (!bundle || effectiveLevelMode !== 'numeric') return Infinity

    // Custom views store explicit per-PA level caps — use those instead of
    // the underlying practice-level associations so the selector respects the
    // caps the view author set rather than what the base practices reach.
    if (isCustomViewActive) {
      const cv = customViews.find((c) => c.id === selectedCustomViewId)
      if (!cv) return Infinity
      let max = 0
      for (const cvpa of cv.practiceAreas) {
        const seq = bundle.levels.find((l) => l.id === cvpa.levelId)?.sequence ?? 0
        if (seq > max) max = seq
      }
      return max > 0 ? max : Infinity
    }

    const practiceIdsInView = new Set(
      bundle.practices.filter((p) => visiblePaIds.has(p.practiceAreaId)).map((p) => p.id)
    )
    const numericLevelIds = new Set(
      bundle.levels.filter((l) => l.category === LevelType.Numeric).map((l) => l.id)
    )
    let max = 0
    for (const pl of bundle.practiceLevels) {
      if (practiceIdsInView.has(pl.practiceId) && numericLevelIds.has(pl.levelId)) {
        const seq = bundle.levels.find((l) => l.id === pl.levelId)?.sequence ?? 0
        if (seq > max) max = seq
      }
    }
    return max > 0 ? max : Infinity
  })()

  // ─── Build the filtered level list for the dropdown ───────────────────────
  const availableLevels = (bundle?.levels ?? [])
    .slice()
    .sort((a, b) => a.sequence - b.sequence)
    .filter((level) => {
      if (effectiveLevelMode === 'mddap') return level.category === LevelType.Mddap
      return level.category === LevelType.Numeric && level.sequence <= maxNumericSequence
    })

  // ─── Auto-select first available level when mode/bundle changes ───────────
  // Also fires when maxNumericSequence shrinks and the current selection falls out of range.
  // The level choice is persisted, so a bundle identity change alone (offline↔online
  // toggle, page refresh, refetch) must not clobber it — snap to the highest level only
  // when the available range actually grows or the current selection is no longer valid.
  const prevMaxNumericSequenceRef = useRef<number | null>(null)
  useEffect(() => {
    if (availableLevels.length === 0) return

    const prevMaxNumericSequence = prevMaxNumericSequenceRef.current
    prevMaxNumericSequenceRef.current = maxNumericSequence

    const stillValid = availableLevels.some((l) => l.id === selectedLevelId)

    if (effectiveLevelMode === 'numeric') {
      // Numeric: track the highest available level when expanding the model selection
      // (which raises maxNumericSequence) so all newly available content shows.
      const rangeGrew =
        prevMaxNumericSequence !== null && maxNumericSequence > prevMaxNumericSequence
      if (!stillValid || rangeGrew) {
        const highest = availableLevels[availableLevels.length - 1]
        if (highest.id !== selectedLevelId) {
          setSelectedLevelId(highest.id)
        }
      }
    } else if (!stillValid) {
      // MDDAP: only correct if the current selection is no longer in the list.
      setSelectedLevelId(availableLevels[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLevelMode, maxNumericSequence, bundle])

  // ─── Model view selection handler ────────────────────────────────────────
  // Guarded: clearing the custom view changes which shared notes are in scope, so an open
  // note with unsaved text would be dropped exactly as it would be by navigating away.
  const handleModelViewClick = (id: number | null) => {
    requestGuardedAction(() => {
      setSelectedCustomViewId(null)
      setSelectedModelViewId(id === selectedModelViewId ? null : id)
      setModelViewOpen(false)
      setCollapsedCategories(new Set())
    })
  }

  // Custom view selection — exclusively selects this view, clears all others
  const handleCustomViewClick = (cvId: number) => {
    requestGuardedAction(() => {
      setSelectedCustomViewId(cvId)
      setModelViewOpen(false)
      setCollapsedCategories(new Set())
    })
  }

  // Handle context multi-select change
  const handleContextChange = (event: any) => {
    const value = event.target.value
    const valueArray = Array.isArray(value) ? value : [value]
    const numericIds = valueArray.map(Number)
    const prev = prevContentTagIdsRef.current

    const hasAllNow = numericIds.includes(-1)
    const hadAllBefore = prev.includes(-1)

    if (hasAllNow && !hadAllBefore) {
      setSelectedContentTagIds([-1])
    } else if (!hasAllNow && hadAllBefore) {
      setSelectedContentTagIds(numericIds.length > 0 ? numericIds : [])
    } else if (hasAllNow && hadAllBefore) {
      setSelectedContentTagIds(numericIds.filter(id => id !== -1))
    } else {
      setSelectedContentTagIds(numericIds)
    }

    prevContentTagIdsRef.current = numericIds
  }

  // Build the render value for the model view dropdown
  const getModelViewLabel = () => {
    if (selectedCustomViewId !== null) {
      const cv = customViews.find((v) => v.id === selectedCustomViewId)
      if (!cv) return t('filterBar.allModelViews')
      if (isOwnedView(cv)) return cv.name
      const ownedBy = t('customViews.ownedBy', { owner: cv.ownerUsername ?? '' })
      return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%' }}>
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{cv.name}</Box>
          <Tooltip title={ownedBy}>
            <PeopleAltOutlinedIcon sx={{ fontSize: '1rem', flexShrink: 0 }} aria-label={ownedBy} />
          </Tooltip>
        </Box>
      )
    }
    if (selectedModelViewId === null) {
      return t('filterBar.allModelViews')
    }
    return bundle?.modelViews.find((mv) => mv.id === selectedModelViewId)?.displayName ?? t('filterBar.allModelViews')
  }

  const renderCustomViewGroup = (
    keyPrefix: string,
    collapseKey: ModelCategory,
    label: string,
    views: ApiCustomView[],
  ) => {
    if (views.length === 0) return []
    const collapsed = collapsedCategories.has(collapseKey)
    const hasSelection = isCustomViewActive && views.some((cv) => cv.id === selectedCustomViewId)
    return [
      <ListSubheader
        key={`${keyPrefix}-header`}
        role="button"
        tabIndex={0}
        onClick={(e) => toggleCategory(collapseKey, e)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') toggleCategory(collapseKey, e)
        }}
        sx={{
          lineHeight: '2rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          userSelect: 'none',
          fontWeight: hasSelection ? 700 : undefined,
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '0.65rem',
            height: '0.65rem',
            fontSize: '0.65rem',
            transition: 'transform 0.15s ease',
            transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
          }}
        >
          <TreeChevronIcon />
        </span>
        {label}
      </ListSubheader>,
      ...(!collapsed
        ? views.map((cv) => (
            <MenuItem
              key={`cv-${cv.id}`}
              value={`cv-${cv.id}`}
              onClick={() => handleCustomViewClick(cv.id)}
              sx={getMultiSelectMenuItemSx(isCustomViewActive && selectedCustomViewId === cv.id)}
            >
              {isOwnedView(cv) ? (
                cv.name
              ) : (
                <ListItemText
                  primary={cv.name}
                  secondary={t('customViews.ownedBy', { owner: cv.ownerUsername ?? '' })}
                  sx={{ my: 0 }}
                  slotProps={{ secondary: { sx: { fontSize: '0.75rem' } } }}
                />
              )}
            </MenuItem>
          ))
        : []),
    ]
  }

  // Level is excluded: it always carries a value (the effect above auto-selects the
  // highest available level), so counting it would show a filter the user never set.
  const activeFilterCount =
    (isCustomViewActive || selectedModelViewId !== null ? 1 : 0) +
    (selectedContentTagIds.length > 0 && !selectedContentTagIds.includes(-1) ? 1 : 0)

  const handleClearFilters = () => {
    requestGuardedAction(() => {
      setSelectedCustomViewId(null)
      setSelectedModelViewId(null)
      setSelectedContentTagIds([-1])
    })
  }

  // Rendered either inline in the wide bar or inside the compact bottom sheet.
  // One copy only: all filter state and handlers stay in this component.
  const filterGroups = (
    <>
      {/* Single-select model view filter (also shows custom views) */}
      <div className="filter-group" data-tour="filter-model-view">
        <label>{t('filterBar.modelView')}</label>
        <Select
          value={isCustomViewActive ? `cv-${selectedCustomViewId}` : (selectedModelViewId ?? -1)}
          onChange={() => {/* all selection handled via onClick on each MenuItem */}}
          open={modelViewOpen}
          onOpen={() => setModelViewOpen(true)}
          onClose={() => {
            if (headerClickInProgressRef.current) {
              headerClickInProgressRef.current = false
              return
            }
            setModelViewOpen(false)
          }}
          input={<OutlinedInput />}
          size="small"
          sx={filterBarSelectSx}
          renderValue={getModelViewLabel}
        >
          {/* "All model views" option */}
          <MenuItem
            value={-1}
            onClick={() => handleModelViewClick(null)}
            sx={getMultiSelectMenuItemSx(!isCustomViewActive && selectedModelViewId === null)}
          >
            {t('filterBar.allModelViews')}
          </MenuItem>

          {/* System model views grouped by category — uncategorized models are hidden */}
          {([
            [ModelCategory.Aim,            'filterBar.categoryAim'],
            [ModelCategory.Domain,         'filterBar.categoryDomain'],
            [ModelCategory.CapabilityArea, 'filterBar.categoryCapabilityArea'],
            [ModelCategory.Mddap,          'filterBar.categoryMddap'],
          ] as const).flatMap(([catValue, labelKey]) => {
            const views = (bundle?.modelViews ?? [])
              .filter((mv) => mv.category === catValue)
              .slice()
              .sort((a, b) => a.sortOrder - b.sortOrder)
            if (views.length === 0) return []
            const isCollapsed = collapsedCategories.has(catValue)
            const hasSelection = !isCustomViewActive && views.some((mv) => mv.id === selectedModelViewId)
            return [
              <ListSubheader
                key={`cat-header-${catValue}`}
                role="button"
                tabIndex={0}
                onClick={(e) => toggleCategory(catValue, e)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') toggleCategory(catValue, e)
                }}
                sx={{
                  lineHeight: '2rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  userSelect: 'none',
                  fontWeight: hasSelection ? 700 : undefined,
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    // inline-flex with a square box keeps the icon concentric
                    // with its container, so the rotation spins about its centre
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '0.65rem',
                    height: '0.65rem',
                    fontSize: '0.65rem',
                    transition: 'transform 0.15s ease',
                    transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                  }}
                >
                  <TreeChevronIcon />
                </span>
                {t(labelKey)}
              </ListSubheader>,
              ...(!isCollapsed
                ? views.map((mv) => {
                    const checked = !isCustomViewActive && selectedModelViewId === mv.id
                    return (
                      <MenuItem
                        key={mv.id}
                        value={mv.id}
                        onClick={() => handleModelViewClick(mv.id)}
                        sx={getMultiSelectMenuItemSx(checked)}
                      >
                        {mv.displayName} ({mv.acronym})
                      </MenuItem>
                    )
                  })
                : []),
            ]
          })}

          {renderCustomViewGroup(
            'cv',
            -1 as unknown as ModelCategory,
            t('filterBar.myCustomViews'),
            customViews.filter(isOwnedView),
          )}
          {renderCustomViewGroup(
            'cv-shared',
            -2 as unknown as ModelCategory,
            t('filterBar.sharedCustomViews'),
            sharingEnabled ? customViews.filter((cv) => !isOwnedView(cv)) : [],
          )}
        </Select>
      </div>

      {/* Single-select level filter — no "All Levels" option */}
      <div className="filter-group" data-tour="filter-level">
        <label htmlFor="filter-level">{t('filterBar.level')}</label>
        <Select
          id="filter-level"
          value={selectedLevelId ?? ''}
          onChange={(e) => setSelectedLevelId(Number(e.target.value))}
          input={<OutlinedInput />}
          size="small"
          sx={filterBarSelectSx}
        >
          {availableLevels.map((level) => (
            <MenuItem key={level.id} value={level.id}>
              {level.displayName}
            </MenuItem>
          ))}
        </Select>
      </div>

      {/* Multi-select context filter */}
      <div className="filter-group" data-tour="filter-context">
        <label>{t('filterBar.context')}</label>
        <Select
          multiple
          value={selectedContentTagIds}
          onChange={handleContextChange}
          input={<OutlinedInput />}
          size="small"
          sx={filterBarSelectSx}
          renderValue={(selected) => {
            if (selected.includes(-1) || selected.length === 0) {
              return t('filterBar.allContexts')
            }
            if (selected.length === 1) {
              const tag = bundle?.contentTags.find((ct) => ct.id === selected[0])
              return (tag?.displayName ?? tag?.name) ?? t('filterBar.allContexts')
            }
            return t('filterBar.contextsCount', { count: selected.length })
          }}
        >
          <MenuItem
            value="-1"
            sx={getMultiSelectMenuItemSx(selectedContentTagIds.includes(-1))}
          >
            <Checkbox checked={selectedContentTagIds.includes(-1)} tabIndex={-1} disableRipple sx={{ mr: 1 }} />
            {t('filterBar.allContexts')}
          </MenuItem>
          {bundle?.contentTags.map((tag) => {
            const checked = selectedContentTagIds.includes(tag.id)
            return (
              <MenuItem
                key={tag.id}
                value={tag.id}
                sx={getMultiSelectMenuItemSx(checked)}
              >
                <Checkbox checked={checked} tabIndex={-1} disableRipple sx={{ mr: 1 }} />
                {tag.displayName ?? tag.name}
              </MenuItem>
            )
          })}
        </Select>
      </div>
    </>
  )

  const actionButtons = (
    <>
      <FeedbackButton />
      <ForceRefreshBundleButton />
      <MediaGalleryButton />
      <BookmarksMenu />
    </>
  )

  const searchBox = (
        <div className="search-wrap" ref={searchWrapRef} data-tour="filter-search">
        <TextField
          className="search-input"
          placeholder={t('filterBar.searchPlaceholder')}
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value)
            setSearchDropdownOpen(true)
          }}
          onFocus={() => setSearchDropdownOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setSearchDropdownOpen(false)
            if (e.key === 'Enter' && searchValue.trim().length >= 2) {
              setSearchDropdownOpen(false)
              navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`)
            }
          }}
          inputProps={{
            'aria-label': t('filterBar.searchPlaceholder'),
            'aria-expanded': showSearchDropdown,
            'aria-haspopup': 'listbox',
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'var(--color-on-dark-70)' }} />
                </InputAdornment>
              ),
              endAdornment: searchValue ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => { setSearchValue(''); setSearchDropdownOpen(false) }}
                    aria-label={t('filterBar.clearSearch')}
                    size="small"
                    edge="end"
                    sx={{ color: 'var(--color-on-dark-70)', '&:hover': { color: 'var(--color-white)' } }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: 'var(--color-white)',
              backgroundColor: 'var(--color-on-dark-12)',
              borderRadius: '4px',
              '& fieldset': { border: '1px solid var(--color-on-dark-30)' },
              '&:hover fieldset': { borderColor: 'var(--color-on-dark-55)' },
              '&.Mui-focused fieldset': { borderColor: 'var(--color-on-dark-80)' },
            },
            '& input::placeholder': { color: 'var(--color-on-dark-60)', opacity: 1 },
          }}
        />

        <label className="search-filter-option">
          <Checkbox
            size="small"
            checked={searchFilteredOnly}
            onChange={(e) => setSearchFilteredOnly(e.target.checked)}
            sx={{ p: '2px', color: 'var(--color-on-dark-70)', '&.Mui-checked': { color: 'var(--color-white)' } }}
          />
          <span style={{ color: 'var(--color-on-dark-85)', fontSize: '0.75rem', userSelect: 'none' }}>
            {t('filterBar.onlySearchFiltered')}
          </span>
        </label>

        {showSearchDropdown && (
          <div className="search-dropdown" role="listbox" aria-label={t('filterBar.searchResults')}>
            {inlineResults.length === 0 ? (
              <div className="search-no-results">{t('search.noResults')}</div>
            ) : (
              inlineResults.map((result, i) => (
                <div
                  key={i}
                  className="search-result"
                  role="option"
                  aria-selected={false}
                  onClick={() => handleResultClick(result)}
                >
                  <div className="result-type">{result.contentType} - {result.title}</div>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: highlightSearchTerm(result.snippet ?? '', debouncedSearch),
                    }}
                  />
                </div>
              ))
            )}
            <div
              className="search-show-all"
              role="button"
              tabIndex={0}
              onClick={() => {
                setSearchDropdownOpen(false)
                navigate(`/search?q=${encodeURIComponent(debouncedSearch)}`)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSearchDropdownOpen(false)
                  navigate(`/search?q=${encodeURIComponent(debouncedSearch)}`)
                }
              }}
            >
              {t('search.showAllResults')}
            </div>
          </div>
        )}
        </div>
  )

  const rightCluster = (
    <div className="filter-bar-right">
      {actionButtons}
      {searchBox}
    </div>
  )

  return (
    <div className={`filter-bar${isCompact ? ' filter-bar--compact' : ''}`} role="search">
      {isCompact && searchExpanded ? (
        <div className="filter-bar-right filter-bar-right--search">
          {searchBox}
          <IconButton
            onClick={() => { setSearchExpanded(false); setSearchDropdownOpen(false) }}
            aria-label={t('filterBar.closeSearch')}
            sx={{ color: 'var(--color-white)', flexShrink: 0 }}
          >
            <ClearIcon />
          </IconButton>
        </div>
      ) : isCompact ? (
        <>
          <Button
            className="filter-sheet-btn"
            startIcon={<TuneIcon />}
            onClick={() => setFilterSheetOpen(true)}
            aria-expanded={filterSheetOpen}
            aria-haspopup="dialog"
            sx={{
              color: 'var(--color-white)',
              flexShrink: 0,
              '&:hover': { backgroundColor: 'var(--color-on-dark-12)' },
            }}
            variant="text"
          >
            {/* MUI primary blue on the brand-darker bar is barely distinguishable;
                amber on black is the same treatment the draft chip uses. */}
            <Badge
              badgeContent={activeFilterCount}
              sx={{
                '& .MuiBadge-badge': {
                  right: -10,
                  backgroundColor: 'var(--color-highlight)',
                  color: 'var(--color-black)',
                  fontWeight: 700,
                },
              }}
            >
              {t('filterBar.filters')}
            </Badge>
          </Button>

          <div className="filter-bar-right">
            {actionButtons}
            <IconButton
              onClick={() => setSearchExpanded(true)}
              aria-label={t('filterBar.openSearch')}
              sx={{ color: 'var(--color-white)', flexShrink: 0 }}
            >
              <SearchIcon />
            </IconButton>
          </div>

          <FilterSheet
            open={filterSheetOpen}
            onClose={() => setFilterSheetOpen(false)}
            activeCount={activeFilterCount}
            onClearAll={handleClearFilters}
          >
            {filterGroups}
          </FilterSheet>
        </>
      ) : (
        <>
          {filterGroups}
          {rightCluster}
        </>
      )}
    </div>
  )
}
